import type Cropper from 'cropperjs';

import type { MNImageEditorDataContent } from '../../../../../../common/elements/elementModelTypes';
import { composeImageEditorTransforms, decomposeImageEditorTransformForEditedImage } from './imageModalEditorDataUtils';
import {
    asFullSourceTransform,
    asSessionTransform,
    type ImageEditorScale,
    type ImageEditorTransform,
    type ImageSize,
    type PersistedImageEditorTransform,
} from './imageModalTransformTypes';

/** Normalises Cropper's optional scale values to the image editor domain. */
const getImageEditorScale = (scale: number | undefined): ImageEditorScale => (scale === -1 ? -1 : 1);

/** Converts persisted image editor data into a complete domain transform. */
export const getPersistedImageEditorTransform = (
    data: MNImageEditorDataContent | null | undefined,
): PersistedImageEditorTransform | null => {
    if (
        !data ||
        !Number.isFinite(data.x) ||
        !Number.isFinite(data.y) ||
        !Number.isFinite(data.width) ||
        !Number.isFinite(data.height)
    ) {
        return null;
    }

    // Persisted `imageEditorData` is authored in full-source absolute space by contract.
    return asFullSourceTransform({
        crop: {
            x: data.x as number,
            y: data.y as number,
            width: data.width as number,
            height: data.height as number,
        },
        rotation: data.rotate ?? 0,
        scaleX: getImageEditorScale(data.scaleX),
        scaleY: getImageEditorScale(data.scaleY),
    });
};

/**
 * Converts live Cropper data into a domain transform, leaving the coordinate space unbranded: which
 * space it is depends on the source the cropper is currently showing, so the caller must tag it (via
 * `asFullSourceTransform` / `asSessionTransform`) at the point that context is known.
 */
export const getImageEditorTransformFromCropperData = (data: Cropper.Data): ImageEditorTransform => ({
    crop: { x: data.x, y: data.y, width: data.width, height: data.height },
    rotation: data.rotate ?? 0,
    scaleX: getImageEditorScale(data.scaleX),
    scaleY: getImageEditorScale(data.scaleY),
});

/** Converts a domain transform back to Cropper data at the adapter boundary. */
export const getCropperDataFromImageEditorTransform = (transform: ImageEditorTransform): Cropper.Data => ({
    ...transform.crop,
    rotate: transform.rotation,
    scaleX: transform.scaleX,
    scaleY: transform.scaleY,
});

export type CropToolToggleSession = {
    rotation: number;
    scaleX: number;
    scaleY: number;
    // The Cropper data to re-apply once the swapped-in source loads, or null when there is no live
    // cropper to carry state over from (the pending snapshot must then be left untouched).
    pendingCropperData: Cropper.Data | null;
};

/**
 * Computes the session transform (and the Cropper data to restore) for a crop-tool toggle.
 *
 * Enabling the crop tool moves from the edited-image-relative space to the full-source space, so the live session
 * edits are composed onto the stored transform; disabling it decomposes the absolute transform back to a
 * edited-image-relative session. Without a live cropper (source still loading) the session simply rewinds to the
 * stored transform (enabling) or identity (disabling).
 */
export const getCropToolToggleSession = ({
    enablingCropTool,
    liveCropperData,
    storedTransform,
    fullSourceSize,
    editedImageSize,
}: {
    enablingCropTool: boolean;
    liveCropperData: Cropper.Data | null | undefined;
    storedTransform: PersistedImageEditorTransform | null;
    fullSourceSize: ImageSize;
    editedImageSize: ImageSize;
}): CropToolToggleSession => {
    if (liveCropperData) {
        const liveTransform = getImageEditorTransformFromCropperData(liveCropperData);
        // Enabling reads the edited render (session space) and composes onto the stored transform;
        // disabling reads the full source (absolute space) and decomposes it back to a session.
        const nextTransform = enablingCropTool
            ? composeImageEditorTransforms(storedTransform, asSessionTransform(liveTransform), fullSourceSize)
            : decomposeImageEditorTransformForEditedImage(
                  storedTransform,
                  asFullSourceTransform(liveTransform),
                  editedImageSize,
              );

        return {
            rotation: nextTransform.rotation,
            scaleX: nextTransform.scaleX,
            scaleY: nextTransform.scaleY,
            pendingCropperData: getCropperDataFromImageEditorTransform(nextTransform),
        };
    }

    if (enablingCropTool) {
        return {
            rotation: storedTransform?.rotation || 0,
            scaleX: storedTransform?.scaleX || 1,
            scaleY: storedTransform?.scaleY || 1,
            pendingCropperData: null,
        };
    }

    return { rotation: 0, scaleX: 1, scaleY: 1, pendingCropperData: null };
};
